import Accordion from '@service-victoria/component-library/components/Accordion';
import Text from '@service-victoria/component-library/components/Text';
import { useWizardContext } from '@service-victoria/component-library/components/Wizard/context';
import { StepComponent } from '@service-victoria/component-library/components/Wizard/props';
import React, { useMemo, useCallback, useEffect } from 'react';

import { ReviewItem } from 'app/components';
import { useRedemptionStepValidation } from 'app/hooks';
import * as Interfaces from 'app/interfaces';
import * as Steps from '..';
import { constants } from 'app/utils';

import ConsentCheckbox from './ConsentCheckbox';
import * as Styled from './styled';

export type ComponentProps = StepComponent<Pick<Interfaces.Common.RedemptionFormValues, 'review'>>;

const EditButton = ({ onClick }: { onClick: () => void }) => {
  return (
    <Styled.EditButton
      iconType='change'
      type='button'
      styleType='largeWithBorder'
      onClick={onClick}>
      Edit
    </Styled.EditButton>
  );
};

const Review: React.FC<ComponentProps> = ({ context }) => {
  useRedemptionStepValidation();
  const { values: _allValues } = context.form;
  const allValues = _allValues as Interfaces.Common.RedemptionFormValues;
  const { formValues, dispatch } = useWizardContext();

  useEffect(() => {
    // on unmount
    return () => {
      // if the form is in an error status we want to clear it
      context.form.setStatus(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onEditClick = useCallback(
    (stepId: string) => () => {
      dispatch({
        type: 'GOTO_STEP',
        stepId,
        values: formValues
      });
    },
    [dispatch, formValues]
  );

  const reviewItems = useMemo(() => {
    if (allValues.redeem.redemptionType === Interfaces.Common.RedemptionType.Individual) {
      return (
        <>
          <ReviewItem
            label='Activity provider: '
            value={allValues.redeem.individual.providerName ?? ''}
          />

          <ReviewItem label='Voucher code: ' value={allValues.redeem.individual.voucherCode} />

          {allValues.redeem.individual.childName && (
            <ReviewItem
              label='Child name (Optional): '
              value={allValues.redeem.individual.childName ?? ''}
            />
          )}

          <ReviewItem label='Activity: ' value={allValues.redeem.individual.activityType} />

          <ReviewItem
            label='Amount: '
            value={
              allValues.redeem.individual.amount.startsWith('$')
                ? allValues.redeem.individual.amount
                : `$${allValues.redeem.individual.amount}`
            }
          />

          <EditButton onClick={onEditClick(Steps.Redeem.Individual.Step.id)} />
        </>
      );
    } else if (allValues.redeem.redemptionType === Interfaces.Common.RedemptionType.Bulk) {
      return (
        <>
          <ReviewItem label='File name: ' value={allValues.redeem.bulk.fileUpload[0]?.name ?? ''} />

          <ReviewItem
            label='Number of Vouchers: '
            value={allValues.redeem.bulk.aggregateData?.totalVouchers.toString() ?? '0'}
          />
          <ReviewItem
            label='Activity type(s): '
            value={allValues.redeem.bulk.aggregateData?.activityTypes.join(', ') ?? ''}
          />

          <ReviewItem
            label='Amount: '
            value={`$${allValues.redeem.bulk.aggregateData?.totalAmount ?? 0}`}
          />

          <EditButton onClick={onEditClick(Steps.Redeem.Bulk.Step.id)} />
        </>
      );
    } else {
      return <React.Fragment />;
    }
  }, [allValues.redeem, onEditClick]);

  return (
    <Styled.Wrapper>
      <Text.Heading.H1 fontWeight={600}>Review</Text.Heading.H1>
      <Styled.Spacer48 />
      <Accordion
        defaultActiveIndex={[0]}
        allowMultiple
        items={[
          {
            id: 'voucher-summary',
            title: 'Voucher summary',
            component: reviewItems
          }
        ]}
      />
      <Styled.Spacer48 />
      <Text.Heading.H2 fontWeight={600}>Consent and Declarations</Text.Heading.H2>
      <ConsentCheckbox fieldName='review.consentDeclared'>
        <Styled.ConsentText>
          I certify and declare that all the information I have provided is correct and complete and
          that I will abide by the terms and conditions laid out in the{' '}
          <Text.Link target='_blank' href={constants.PROGRAM_GUIDELINES_URL}>
            Get Active Kids Guidelines
          </Text.Link>
          .
        </Styled.ConsentText>
      </ConsentCheckbox>
    </Styled.Wrapper>
  );
};

export default Review;
